import sanitizeHtml from 'sanitize-html';
import { useRef, useState } from 'react';

function stripLinksFromHtml(html) {
    if (!html) return html;
    return sanitizeHtml(html, {
        allowedTags: sanitizeHtml.defaults.allowedTags.filter(tag => tag !== 'a'),
        disallowedTagsMode: 'recursiveEscape',
        allowedAttributes: {
            '*': ['style'],
            img: ['src', 'alt', 'width', 'height'],
        },
    });
}

function sanitize(dirty) {
    return sanitizeHtml(dirty, {
        allowedTags: sanitizeHtml.defaults.allowedTags.concat([
            'img',
            'iframe',
        ]),
        allowedAttributes: {
            '*': ['style'],
            a: ['href', 'target', 'rel'],
            img: ['src', 'alt', 'width', 'height'],
            iframe: [
                'src',
                'width',
                'height',
                'frameborder',
                'allow',
                'allowfullscreen',
                'title',
            ],
        },
        allowedStyles: {
            '*': {
                // Match HEX and RGB
                'background-color': [
                    /^#(0x)?[0-9a-f]+$/i,
                    /^[a-z]+$/i,
                    /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/,
                ],
                color: [
                    /^#(0x)?[0-9a-f]+$/i,
                    /^[a-z]+$/i,
                    /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/,
                ],
                'border-radius': [/^\d+(?:px|em|%)$/],
                'text-align': [/^left$/, /^right$/, /^center$/],
                // Match any number with px, em, or %
                'font-size': [/^\d+(?:px|em|%)$/],
                margin: [/.*/],
                padding: [/.*/],
            },
            p: {
                'font-size': [/^\d+rem$/],
            },
        },
        allowedIframeHostnames: ['www.google.com'],
        // Keep in sync with the platform list shown in EditVideoEmbedForm.jsx
        allowedIframeDomains: [
            'youtube.com',
            'youtube-nocookie.com',
            'vimeo.com',
            'loom.com',
            'wistia.com',
            'wistia.net',
            'tella.com',
            'tella.tv',
            'livid.com',
        ],
        transformTags: {
            'a': function(tagName, attribs) {
                // Check if the link has a target="_blank" attribute
                if (attribs.target === '_blank') {
                    // Add "noopener noreferrer" to the rel attribute
                    // Ensure existing rel values (like nofollow) are preserved if needed
                    let rel = attribs.rel ? attribs.rel.split(' ') : [];
                    if (!rel.includes('noopener')) rel.push('noopener');
                    if (!rel.includes('noreferrer')) rel.push('noreferrer');
                    attribs.rel = rel.join(' ');
                }
                return {
                    tagName: tagName,
                    attribs: attribs
                };
            }
        }
    });
}

const SafeHtml = ({ html, iframe, stripLinks }) => {
    const [iframeHeight, setIframeHeight] = useState();
    const iframeRef = useRef();
    const cleaned = stripLinks ? stripLinksFromHtml(html) : html;
    const sanitized = sanitize(cleaned);
    if (iframe) {
        const css = `<style type="text/css">html { font-family: ui-rounded, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif; }</style>`;
        return (
            <iframe
                ref={iframeRef}
                srcDoc={css + sanitized}
                className="w-full h-fit scrollbar-visible min-h-64"
                style={{ height: `${iframeHeight}px` }}
                onLoad={() => {
                    setIframeHeight(iframeRef.current?.contentWindow?.document.body.scrollHeight + 32);
                }}
            />
        );
    } else {
        return (
            <div
                dangerouslySetInnerHTML={{ __html: sanitized }}
                className="whitespace-pre-wrap rendered-html"
            ></div>
        );
    }

};

export default SafeHtml;
